import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  MapPin,
  Mail,
  Phone,
  Instagram,
  MessageCircle,
  Send,
  Loader2,
  Clock,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { BRAND, whatsappLink } from "../data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa nombre, email y mensaje.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("No pudimos enviar tu mensaje. Inténtalo nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative scroll-mt-24 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#1E3A8A]">
              Contacto
            </span>
            <h2 className="mt-3 font-display text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Estamos aquí para tu mascota
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">
              Escríbenos por el formulario o contáctanos directamente. Para
              urgencias, llámanos por WhatsApp.
            </p>

            <div className="mt-8 space-y-3">
              <a
                href={BRAND.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address"
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#1E3A8A]/8 text-[#1E3A8A]">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    Dirección
                  </div>
                  <div className="text-sm text-slate-600">{BRAND.address}</div>
                </div>
              </a>

              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-phone"
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    Teléfono / WhatsApp
                  </div>
                  <div className="text-sm text-slate-600">{BRAND.phone}</div>
                </div>
              </a>

              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email"
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#EA580C]/10 text-[#EA580C]">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    Email
                  </div>
                  <div className="text-sm text-slate-600">{BRAND.email}</div>
                </div>
              </a>

              <a
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-pink-500/10 text-pink-600">
                  <Instagram className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    Instagram
                  </div>
                  <div className="text-sm text-slate-600">@{BRAND.instagram}</div>
                </div>
              </a>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
              <iframe
                title="CEQVet en Google Maps"
                data-testid="contact-map"
                src={BRAND.mapsEmbed}
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#1E3A8A]/5 p-4">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#1E3A8A] text-white">
                <Clock className="h-5 w-5" />
              </span>
              <div className="text-sm text-slate-700">
                <div className="font-medium text-slate-900">
                  Horarios de atención
                </div>
                <ul className="mt-1 space-y-0.5 text-slate-600">
                  {BRAND.hours.map((h) => (
                    <li key={h.day}>
                      <span className="font-medium text-slate-800">
                        {h.day}:
                      </span>{" "}
                      {h.time}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8"
            >
              <h3 className="font-display text-2xl font-medium tracking-tight text-slate-900">
                Envíanos un mensaje
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Te responderemos a la brevedad por email o WhatsApp.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="c-name">Nombre completo</Label>
                  <Input
                    id="c-name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    className="mt-1.5 rounded-xl"
                    data-testid="contact-input-name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input
                    id="c-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="mt-1.5 rounded-xl"
                    data-testid="contact-input-email"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="c-phone">Teléfono (opcional)</Label>
                  <Input
                    id="c-phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+56 ..."
                    className="mt-1.5 rounded-xl"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="c-message">Mensaje</Label>
                  <Textarea
                    id="c-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                    rows={5}
                    className="mt-1.5 rounded-xl"
                    data-testid="contact-input-message"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="rounded-full bg-[#1E3A8A] px-6 text-white hover:bg-[#172554]"
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Enviar mensaje
                </Button>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <MessageCircle className="h-4 w-4 text-[#25D366]" /> Chatear
                  por WhatsApp
                </a>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
